import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { FormGroup } from '../../../../components/form/FormGroup';
import { useNamespacedTranslation } from '../../../../hooks/useNamespacedTranslation';
import { ContentDocumentLinkDataFragment } from '../../__generated__/graphql';
import FlairIcon from '../../../../atomic/atoms/FlairIcon';
import {
  DocumentPreview,
  isPreviewableType,
} from '../../pages/Documents2/DocumentInfo/DocumentPreview';

type Props = {
  contentDocumentLinks: ReadonlyArray<ContentDocumentLinkDataFragment>;
};

const i18Path = 'approvalRequest.detailsSidebar';
const previewI18Path = 'documents2.documentPreview';

const fileNameOf = (link: ContentDocumentLinkDataFragment): string =>
  link.ContentDocument.FileExtension
    ? `${link.ContentDocument.Title}.${link.ContentDocument.FileExtension}`
    : link.ContentDocument.Title;

const openExternal = (url?: string | null) => {
  if (url) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

export const AttachmentsPreview: React.FC<Props> = ({
  contentDocumentLinks,
}) => {
  const t = useNamespacedTranslation(i18Path);
  const tPreview = useNamespacedTranslation(previewI18Path);
  const [previewed, setPreviewed] =
    useState<ContentDocumentLinkDataFragment | null>(null);

  if (!contentDocumentLinks || contentDocumentLinks.length === 0) {
    return null;
  }

  const handleClick = (link: ContentDocumentLinkDataFragment) => {
    if (isPreviewableType(link.ContentDocument.FileType) && link.viewUrl) {
      setPreviewed(link);
    } else {
      openExternal(link.downloadUrl);
    }
  };

  return (
    <Row className="mt-2">
      <Col>
        <FormGroup>
          <Form.Label className="fw-bold">{t('attachments')}</Form.Label>
          {contentDocumentLinks.map((link) => (
            <div key={link.Id}>
              <button
                type="button"
                className="btn btn-link p-0 text-decoration-none align-baseline"
                onClick={() => handleClick(link)}>
                <FlairIcon size="xs" icon="document-outline" className="me-2" />
                {fileNameOf(link)}
              </button>
            </div>
          ))}
        </FormGroup>
      </Col>

      <PreviewModal
        show={!!previewed}
        onHide={() => setPreviewed(null)}
        dialogClassName="break-modal-full"
        centered
        contentClassName="border">
        <Modal.Header className="card-header d-flex align-items-center justify-content-between">
          <h2 className="card-header-title mb-0">
            {previewed ? fileNameOf(previewed) : ''}
          </h2>
          <FlairIcon icon="close-outline" onClick={() => setPreviewed(null)} />
        </Modal.Header>
        <Modal.Body className="p-0 position-relative" style={{ height: '87%' }}>
          <div className="position-relative w-100 h-100">
            {previewed && (
              <DocumentPreview
                type={previewed.ContentDocument.FileType}
                url={previewed.viewUrl ?? ''}
                width="100%"
              />
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          {previewed?.downloadUrl && (
            <Button
              variant="primary"
              onClick={() => openExternal(previewed.downloadUrl)}>
              <span className="small">{tPreview('buttons.download')}</span>
            </Button>
          )}
        </Modal.Footer>
      </PreviewModal>
    </Row>
  );
};

const PreviewModal = styled(Modal)`
  .modal-content {
    height: 90vh;
  }
`;
